import React, { useState, useEffect, useRef } from 'react';
import { Snackbar, Alert } from '@mui/material';
import {
  Typography,
  TextField,
  MenuItem,
  Button,
  Box,
  CircularProgress,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Pagination,
  Autocomplete,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import ConversationDialog from './Dialog';
import { useNavigate } from 'react-router-dom';
import CustomerLayout from './CustomerLayout';
import WithAuth from './WithAuth';
import * as flatted from 'flatted';


const Customers = ({
  header = 'View/Edit Customers',
  refresh,
  merge = false,
  onDelete = false,
  handleDeleteMany,
  handleMergeMany,
  trash = false,
  recent = true,
  propsData
}) => {
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [clusteredData, setClusteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showMergeForm, setShowMergeForm] = useState(false);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [checkedRows, setCheckedRows] = useState([]);
  const [mergeFormData, setMergeFormData] = useState({
    name: '',
    Role: '',
    stDepartment: '',
    internalOrExternal: '',
    school: '',
    city: '',
    state: '',
    country: '',
    location: '',
    ContactNumbers: '',
    emailIds: '',
    programsAttended: '',
    links: '',
  });
  const [filters, setFilters] = useState({
    uniqueMergeId: '',
    uploadId: '',
    contactId: '',
    name: '',
    Role: '',
    stDepartment: '',
    internalOrExternal: '',
    school: '',
    city: '',
    state: '',
    country: '',
    location: '',
    ContactNumbers: '',
    emailIds: '',
    programsAttended: '',
    links: '',
  });

  const [selectedConversations, setSelectedConversations] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [isAddingConversation, setIsAddingConversation] = useState([false, undefined]);
  const [newConversation, setNewConversation] = useState({
    _id: '',
    title: '',
    conversationText: '',
    STResource: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(50);
  const [totalDocs, setTotalDocs] = useState(0);
  const [selectAll, setSelectAll] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [open, setOpen] = useState(false);
  const filterTimeout = useRef(null);
  const [masterData, setMasterData] = useState([]);  // full list, once
  const [clusterPage, setClusterPage] = useState(1);
  const [showClusteredView, setShowClusteredView] = useState(false);
  const clusterRowsPerPage = 50;


  useEffect(() => {
    const fetchMaster = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/customers/contacts?limit=0`
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const { rows } = await res.json();
        setMasterData(rows);                          // all contacts
      } catch (err) {
        console.error('⚠️ fetchMaster failed:', err);
      }
    };

    fetchMaster();        // runs once on mount
  }, []);

  const [mlStatus, setMlStatus] = useState(null); // null = unknown, true = online, false = 
  const hasActiveFilters = (f) =>
    Object.values(f).some(
      (v) => v !== '' && v !== null && v !== undefined && v !== false
    );


  useEffect(() => {
    const checkBackend = async () => {
      try {
        const response = await fetch('http://localhost:5001/'); // Flask base route
        if (response.ok) {
          setMlStatus(true); // backend is up
        } else {
          setMlStatus(false); // backend is reachable but unhealthy
        }
      } catch (err) {
        setMlStatus(false); // backend not reachable
      }
    };

    checkBackend();
    const interval = setInterval(checkBackend, 10000); // check every 10 seconds
    return () => clearInterval(interval); // cleanup
  }, []);


  const handleCluster = async () => {
    setLoading(true);
    setError('');

    try {
      /* 1️⃣  Pull every contact (no limit) */
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/customers/contacts?limit=0`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status} while loading contacts`);

      const { rows } = await res.json();          // all docs, not just 50

      /* 2️⃣  Massage the data for the ML service */
      const cleanedData = rows.map((c) => {
        const out = {};
        for (const k in c) {
          let v = c[k];

          if (Array.isArray(v)) out[k] = v.join(', ');
          else if (typeof v === 'object' && v !== null) out[k] = JSON.stringify(v);
          else if (typeof v === 'number' && isNaN(v)) out[k] = '';
          else out[k] = v ?? '';
        }

        // fix location corner-cases
        if (!out.location || typeof out.location !== 'string' ||
          out.location.toLowerCase() === 'nan nan') {
          out.location = '';
        }
        return out;
      });

      /* 3️⃣  Send to Flask */
      const ml = await fetch('http://localhost:5001/cluster', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanedData)
      });
      if (!ml.ok) throw new Error('Clustering failed');

      const clusters = await ml.json();

      /* 4️⃣  Show the first 50 clusters (keep UI consistent) */

      setClusteredData(clusters);
      setClusterPage(1);               // start on page 1
      setShowClusteredView(true);

    } catch (err) {
      console.error('Clustering error:', err);
      setError(err.message || 'Error during clustering');
    } finally {
      setLoading(false);
    }
  };

  const paginatedClusters = clusteredData.slice(
    (clusterPage - 1) * clusterRowsPerPage,
    clusterPage * clusterRowsPerPage
  );


  const handleRowClick = (row) => {
    setSelectedRow(row);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };


  useEffect(() => {
    fetchContacts();
  }, [refresh]);

  // Function to convert filteredData to CSV format
  const convertToCSV = (data) => {
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map((row) =>
      Object.values(row)
        .map((value) => {
          if (Array.isArray(value)) {
            return value.join('; '); // Handle array fields (e.g., ContactNumbers, emailIds)
          }
          return `"${value}"`; // Wrap values in quotes to handle commas
        })
        .join(',')
    );
    return [headers, ...rows].join('\n');
  };

  // Function to trigger CSV download
  const handleDownloadCSV = () => {
    if (filteredData.length === 0) {
      alert('No data to download.');
      return;
    }

    const csvData = convertToCSV(filteredData);
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'customers.csv';
    link.click();
    URL.revokeObjectURL(url); // Clean up
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const paginatedRows = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handleCheckboxChange = (rowId) => {
    setCheckedRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );
  };


  const applyFrontendFilters = (data, filters) => {
    return data.filter((row) => {
      return Object.keys(filters).every((key) => {
        const filterValue = filters[key];
        if (!filterValue) return true;

        const rowValue = Array.isArray(row[key])
          ? row[key].join(', ').toLowerCase()
          : row[key]?.toString().toLowerCase() || '';

        return rowValue.includes(filterValue.toLowerCase());
      });
    });
  };


  const fetchContacts = async (resetPage = false) => {
    try {
      /* ---------- decide if we need the full list ---------- */
      const filtersOn = hasActiveFilters(filters);

      /* ---------- build query-string ---------- */
      const qs = new URLSearchParams({
        page: filtersOn ? 1 : page,        // page matters only w/o filters
        limit: filtersOn ? 0 : rowsPerPage, // 0 = “no limit” (we added this in the controller)
        sortBy: sortBy || 'name',
        order: sortOrder,
      });

      /* ---------- fire the request ---------- */
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/customers/contacts?${qs}`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const { rows, total } = await res.json();

      /* ---------- filter client-side if needed ---------- */
      const visibleRows = filtersOn ? applyFrontendFilters(rows, filters) : rows;

      /* ---------- update state ---------- */
      setData(rows);                       // raw data (may be all rows)
      setFilteredData(visibleRows);        // what the table will display
      setTotalDocs(filtersOn ? visibleRows.length : total);

      if (resetPage) setPage(1);           // jump back to page 1 after filter change
    } catch (err) {
      console.error('⚠️ fetchContacts failed:', err);
      setError('Could not load contacts');
    }
  };


  /* Re-run whenever page, sort, or search changes */
  useEffect(() => {
    fetchContacts();
  }, [page, sortBy, sortOrder, refresh]);
  /* Re-run when any filter value changes */
  useEffect(() => {
    fetchContacts(true);                   // resetPage=true
  }, [filters]);

  /* 1️⃣  Re-compute visible rows whenever DATA or FILTERS change */
  useEffect(() => {
    setFilteredData(applyFrontendFilters(data, filters));
    setPage(1);                    // optional: jump back to page 1
  }, [data, filters]);


  const fetchConversations = async (_id) => {
    console.log('fetching conversations of customer with id: ' + _id);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/conversations/${_id}`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error('Error fetching conversations');
      }

      const conversations = await response.json();
      setSelectedConversations(conversations);
      setIsAddingConversation((prev) => [false, prev[1]]);
      setOpenDialog(true);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    clearTimeout(filterTimeout.current);
    filterTimeout.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, [field]: value }));
      /* no need to call setPage here; fetchContacts(true) does it */
    }, 300);
  };

  const getUniqueOptions = (key) => {
    // use masterData if we have it, otherwise fall back to current page
    const source = masterData.length ? masterData : data;
    const opts = source.map((row) => row[key]).flat();
    return [...new Set(opts.filter(Boolean))].sort();
  };

  const handleAddConversation = async (_id) => {
    console.log('=======' + _id);
    setNewConversation((prev) => ({
      ...prev,
      _id: isAddingConversation[1],
    }));
    console.log('_id: ' + isAddingConversation[1]);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/conversations/add`, {
        method: 'POST',
        body: JSON.stringify(newConversation),
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Error adding conversation');
      }

      alert('Conversation added successfully');
      setNewConversation((prev) => ({
        _id: isAddingConversation[1],
        title: '',
        conversationText: '',
        STResource: '',
      }));
    } catch (error) {
      console.error('Error adding conversation:', error);
    }
  };

  const handleDeleteConversation = async (id, customerId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/conversations/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Error deleting conversation');
      }

      fetchConversations(customerId);
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const handleEdit = (row) => {
    console.log(`Edit customer with ID: ${row._id}`);
    let obj = { ...row, latitude: row.location.split(' ')[0], longitude: row.location.split(' ')[1] };
    navigate('/customers/update-customer/specific', { state: { update: true, formData: obj } });
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this customer?');

    if (!confirmDelete) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/customers/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error deleting customer:', errorData);
        throw new Error(errorData.message || 'Failed to delete the customer');
      }

      alert('Customer deleted successfully!');
      console.log('Deleted customer with ID:', id);
      fetchContacts();
    } catch (error) {
      console.error('Error deleting customer:', error);
      alert(`Failed to delete customer: ${error.message}`);
    }
  };

  const handleMerge = () => {
    setShowMergeForm(true); // Show the form when Merge is clicked
    setTimeout(() => {
      const element = document.getElementById('mergedCustomerData');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };


  const handleMergeFormChange = (e) => {
    const { name, value } = e.target;
    setMergeFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleMergeFormSubmit = async (e) => {
    e.preventDefault();
    console.log('checked rows: ' + checkedRows)
    await handleMergeMany({ ...mergeFormData }, checkedRows);
    setShowMergeForm(false); // Hide form after submission
    setCheckedRows([]);
  };


  const handleCheckedDelete = async () => {
    console.log('handle checked delete');
    if (checkedRows.length == 0) return;
    await handleDeleteMany(checkedRows);
    fetchContacts();
  };

  const replacerFunc = () => {
    const visited = new WeakSet();
    return (key, value) => {
      if (typeof value === "object" && value !== null) {
        if (visited.has(value)) {
          return;
        }
        visited.add(value);
      }
      return value;
    };
  };


  const handleRecents = async (setRecent) => {
    if (checkedRows.length === 0) {
      alert("No rows selected!");
      return;
    }

    console.log('pushing to recents....' + checkedRows)

    try {
      const response = await fetch(process.env.REACT_APP_API_URL + "/api/customers/recents", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ uniqueMergeIds: checkedRows, recent: Boolean(setRecent) }, replacerFunc()), // Send recent value dynamically
      });


      const data = await response.json();

      if (response.ok) {
        alert(`Updated recent status successfully!`);
        // Optionally, refresh data
      } else {
        alert(`Error: ${data.message}`);
      }
    } catch (error) {
      console.error("Error updating recent status:", error);
      alert("An error occurred while updating.");
    }
  };


  const handleSelectAll = () => {
    const allIds = paginatedRows.map((row) => row.uniqueMergeId);
    setCheckedRows((prevSelected) =>
      prevSelected.length === paginatedRows.length ? [] : allIds
    );
  };

  const handleGrouping = async () => {
    let res = await fetch(process.env.REACT_APP_API_URL + '/api/customers/group')
    console.log(res);
    fetchContacts();
  }

  const handleDeselectAll = () => {
    setCheckedRows([]);
  };

  const getMergedContacts = (id) => {
    navigate('/customers/mergeId/' + id);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setSnackbarOpen(true); // Show notification
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
      });
  };


  const isSelected = (id) => checkedRows.includes(id);

  const resolvedClustersFull = clusteredData.map((clusterRow) => {
    const idToMatch = clusterRow.contactId || clusterRow._id;
    const matchedContact = masterData.find((m) => String(m._id) === String(idToMatch));
    if (!matchedContact) return null;

    return {
      ...matchedContact,
      cluster: clusterRow.cluster
    };
  }).filter(Boolean);

  // Then apply pagination
  const paginatedResolvedClusters = resolvedClustersFull.slice(
    (clusterPage - 1) * clusterRowsPerPage,
    clusterPage * clusterRowsPerPage
  );
  const getClusterSize = (clusterId) => {
    return clusteredData.filter((item) => item.cluster === clusterId).length;
  };



  const cleanValue = (val) => val === 'N/A' || val === null || val === undefined ? '' : val;


  return (
    <CustomerLayout>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Copied to clipboard!
        </Alert>
      </Snackbar>
      <div style={{ padding: '20px' }}>
        <Typography color="primary" variant="h6" style={{ marginTop: '60px', marginLeft: '20px', marginBottom: '10px', fontWeight: 'bold' }}>
          {header}
        </Typography>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
          {Object.keys(filters).map((key) => (
            <Autocomplete
              key={key}
              options={getUniqueOptions(key)}
              getOptionLabel={(option) => option.toString()}
              // value={filters[key] || ''}
              value={filters[key] || null}

              onChange={(e, value) => handleFilterChange(key, value || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
                  variant="outlined"
                  fullWidth
                  style={{ flex: '1 1 calc(33.33% - 10px)', minWidth: '200px' }}
                />

              )}
            />
          ))}
          <Autocomplete
            options={['name', 'createdAt']}
            value={sortBy}
            onChange={(e, value) => setSortBy(value)}
            renderInput={(params) => (
              <TextField {...params} label="Sort By" />
            )}
          />

          <Autocomplete
            options={['asc', 'desc']}
            value={sortOrder}
            onChange={(e, value) => setSortOrder(value)}
            renderInput={(params) => (
              <TextField {...params} label="Sort Order" />
            )}
          />
          <Box p={4}>
            <Typography variant="h5" gutterBottom>
              Contact Clustering with ML
            </Typography>

            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
              ML Cluster Status
              <Tooltip title={`ML Server: ${mlStatus === null ? 'Checking...' : mlStatus ? 'Online' : 'Offline'}`}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: mlStatus === null ? 'grey' : mlStatus ? 'green' : 'yellow',
                    ml: 1,
                    border: '1px solid black',
                  }}
                />
              </Tooltip>
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCluster}
                disabled={loading}
              >
                {loading ? 'Clustering...' : 'Run Clustering'}
              </Button>

              {showClusteredView && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setShowClusteredView(false)}
                >
                  Back to View/Edit Contacts
                </Button>
              )}
            </Box>

            {error && (
              <Typography color="error" mt={2}>
                {error}
              </Typography>
            )}

            {loading && <CircularProgress sx={{ mt: 2 }} />}


            {showClusteredView && paginatedClusters.length > 0 && (
              <TableContainer
                component={Paper}
                style={{
                  overflowX: 'auto',
                  minWidth: '1500px',
                  // padding: '8px 12px',
                  padding: 2,
                }}
              >

                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Contact ID</TableCell>
                      <TableCell>Cluster</TableCell>
                      <TableCell>Cluster Size</TableCell>
                      <TableCell>Upload ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>stDepartment</TableCell>
                      <TableCell>Internal/External</TableCell>
                      <TableCell>School</TableCell>
                      <TableCell>City</TableCell>
                      <TableCell>State</TableCell>
                      <TableCell>Country</TableCell>
                      <TableCell>Location</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedClusters.map((item, index) => (

                      <TableRow key={index} style={{ cursor: 'pointer', height: '64px' }}>
                        <TableCell sx={{ px: 3 }}>{item?.contactId || 'N/A'}</TableCell>
                        <TableCell sx={{ px: 3 }}>{item?.cluster || 'N/A'}</TableCell>
                        <TableCell sx={{ px: 3 }}>{getClusterSize(item?.cluster)}</TableCell>
                        <TableCell sx={{ px: 3 }}>{item?.uploadId || 'N/A'}</TableCell>
                        <TableCell sx={{ px: 3 }}>{cleanValue(item?.name)}</TableCell>
                        <TableCell sx={{ px: 3 }}>{item?.Role || item?.role || 'N/A'}</TableCell>
                        <TableCell sx={{ px: 3 }}>{item?.stDepartment || item?.StDepartment || 'N/A'}</TableCell>
                        <TableCell sx={{ px: 3 }}>{item?.internalOrExternal || 'N/A'}</TableCell>
                        <TableCell sx={{ px: 3 }}>{cleanValue(item?.school)}</TableCell>
                        <TableCell sx={{ px: 3 }}>{cleanValue(item?.city)}</TableCell>
                        <TableCell sx={{ px: 3 }}>{cleanValue(item?.state)}</TableCell>
                        <TableCell sx={{ px: 3 }}>{cleanValue(item?.country)}</TableCell>
                        <TableCell sx={{ px: 3 }}>{cleanValue(item?.location)}</TableCell>

                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {clusteredData.length > clusterRowsPerPage && (
              <Pagination
                sx={{ mt: 2 }}
                page={clusterPage}
                count={Math.ceil(clusteredData.length / clusterRowsPerPage)}
                onChange={(_, p) => setClusterPage(p)}
              />
            )}
          </Box>
        </div>
        {!onDelete && !merge && <Button onClick={handleDownloadCSV}>Download CSV</Button>}
        {!onDelete && !merge && <Button onClick={handleGrouping}>Group Data</Button>}

        <>
          {checkedRows.length != 0 && merge && <Button onClick={handleMerge}>{'Merge'}</Button>}
          {onDelete && checkedRows.length != 0 && <Button onClick={handleCheckedDelete}>{'Delete'}</Button>}
          {checkedRows.length != 0 && recent && <Button onClick={handleRecents}>{'Add to Recents'}</Button>}
          {(merge || onDelete) && (
            <Button onClick={selectAll ? handleDeselectAll : handleSelectAll}>
              {paginatedRows.length == 0 ? '' : checkedRows.length === paginatedRows.length ? 'Deselect All' : 'Select All'}
            </Button>
          )}
        </>
        {!showClusteredView && (
          <TableContainer
            component={Paper}
            style={{
              overflowX: 'auto',
              maxWidth: '100%',
              minWidth: '1500px', // You can adjust this width
            }}
          >

            <Table>
              <TableHead>
                <TableRow>
                  {(merge || onDelete || recent) && <TableCell></TableCell>}
                  <TableCell>Contact ID</TableCell>
                  <TableCell>Cluster</TableCell>
                  <TableCell>Upload ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>stDepartment</TableCell>
                  <TableCell>Internal/External</TableCell>
                  <TableCell>School</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>School Location</TableCell>
                  <TableCell>Contact Numbers</TableCell>
                  <TableCell>Email IDs</TableCell>
                  <TableCell>Programs Attended</TableCell>
                  <TableCell>Links</TableCell>
                  <TableCell>Conversations</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((row, index) => (
                  <TableRow key={index} onClick={() => handleRowClick(row)} style={{ cursor: "pointer" }}>
                    {(merge || onDelete || recent) && (
                      <TableCell>
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Checkbox
                                onClick={(e) => e.stopPropagation()} // Prevent row click when clicking the checkbox
                                checked={isSelected(row.uniqueMergeId) || checkedRows.includes(row.uniqueMergeId)}
                                onChange={(e) => { e.stopPropagation(); handleCheckboxChange(row.uniqueMergeId) }}
                                color="primary"
                              />
                            }
                            label=""
                          />
                        </FormGroup>
                      </TableCell>
                    )}

                    <TableCell style={{ cursor: 'pointer' }} onClick={() => getMergedContacts(row.uniqueMergeId)}>
                      {row.uniqueMergeId}
                    </TableCell>
                    <TableCell>
                      <span onClick={(e) => { e.stopPropagation(); copyToClipboard(row.cluster) }} style={{ cursor: 'pointer' }}>
                        {row.cluster}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span onClick={(e) => { e.stopPropagation(); copyToClipboard(row.uploadId) }} style={{ cursor: 'pointer' }}>
                        {row.uploadId}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span onClick={(e) => { e.stopPropagation(); copyToClipboard(row.name) }} style={{ cursor: 'pointer' }}>
                        {row.name}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span onClick={(e) => { e.stopPropagation(); copyToClipboard(row.Role) }} style={{ cursor: 'pointer' }}>
                        {row.Role}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span onClick={(e) => { e.stopPropagation(); copyToClipboard(row.stDepartment) }} style={{ cursor: 'pointer' }}>
                        {row.stDepartment}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span onClick={(e) => { e.stopPropagation(); copyToClipboard(row.internalOrExternal) }} style={{ cursor: 'pointer' }}>
                        {row.internalOrExternal}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span onClick={(e) => { e.stopPropagation(); copyToClipboard(row.school) }} style={{ cursor: 'pointer' }}>
                        {row.school}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span onClick={(e) => { e.stopPropagation(); copyToClipboard(row.city) }} style={{ cursor: 'pointer' }}>
                        {row.city}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span onClick={(e) => { e.stopPropagation(); copyToClipboard(row.state) }} style={{ cursor: 'pointer' }}>
                        {row.state}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span onClick={(e) => { e.stopPropagation(); copyToClipboard(row.country) }} style={{ cursor: 'pointer' }}>
                        {row.country}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span onClick={(e) => { e.stopPropagation(); copyToClipboard(row.location) }} style={{ cursor: 'pointer' }}>
                        {row.location}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span onClick={(e) => { e.stopPropagation(); copyToClipboard(row.ContactNumbers.join(', ')) }} style={{ cursor: 'pointer' }}>
                        {row.ContactNumbers.join(', ')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span onClick={(e) => { e.stopPropagation(); copyToClipboard(row.emailIds.join(', ')) }} style={{ cursor: 'pointer' }}>
                        {row.emailIds.join(', ')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span onClick={(e) => { e.stopPropagation(); copyToClipboard(row.programsAttended.join(', ')) }} style={{ cursor: 'pointer' }}>
                        {row.programsAttended.join(', ')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span onClick={(e) => { e.stopPropagation(); copyToClipboard(row.links.join(', ')) }} style={{ cursor: 'pointer' }}>
                        {row.links.join(', ')}
                      </span>
                    </TableCell>


                    <TableCell>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsAddingConversation([false, row._id]);
                          fetchConversations(row._id);
                        }}
                      >
                        Show Conversations
                      </Button>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsAddingConversation([true, row._id]);
                          setNewConversation({
                            _id: row._id,
                            title: '',
                            conversationText: '',
                            STResource: '',
                          });
                          setOpenDialog(true);
                        }}
                      >
                        Add Conversations
                      </Button>
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEdit(row)}>
                        <Edit />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(row._id)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}


      </div>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Row Details</DialogTitle>
        <DialogContent>
          {selectedRow && (
            <>
              <Typography><strong>Cluster:</strong> {selectedRow.cluster}</Typography>
              <Typography><strong>Upload ID:</strong> {selectedRow.uploadId}</Typography>
              <Typography><strong>Name:</strong> {selectedRow.name}</Typography>
              <Typography><strong>Role:</strong> {selectedRow.Role}</Typography>
              <Typography><strong>Department:</strong> {selectedRow.stDepartment}</Typography>
              <Typography><strong>Internal/External:</strong> {selectedRow.internalOrExternal}</Typography>
              <Typography><strong>School:</strong> {selectedRow.school}</Typography>
              <Typography><strong>City:</strong> {selectedRow.city}</Typography>
              <Typography><strong>State:</strong> {selectedRow.state}</Typography>
              <Typography><strong>Country:</strong> {selectedRow.country}</Typography>
              <Typography><strong>Location:</strong> {selectedRow.location}</Typography>
              <Typography><strong>Contact Numbers:</strong> {selectedRow.ContactNumbers.join(", ")}</Typography>
              <Typography><strong>Email IDs:</strong> {selectedRow.emailIds.join(", ")}</Typography>
              <Typography><strong>Programs Attended:</strong> {selectedRow.programsAttended.join(", ")}</Typography>
              <Typography><strong>Links:</strong> {selectedRow.links.join(", ")}</Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {showMergeForm && (
        <div style={{ marginTop: '20px', padding: '20px', border: '1px solid #ccc', borderRadius: '5px' }} id='mergedCustomerData'>
          <Typography variant="h6" gutterBottom>Merged Customer Data</Typography>
          <form onSubmit={handleMergeFormSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {Object.keys(mergeFormData).map((key, index) => (
                <TextField
                  key={key}
                  label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  name={key}
                  value={mergeFormData[key]}
                  onChange={handleMergeFormChange}
                  variant="outlined"
                  fullWidth
                  style={{ flex: '1 1 calc(33.33% - 10px)' }} // Ensures 3 inputs per row
                />
              ))}
            </div>
            <Button type="submit" variant="contained" color="primary">Submit</Button>
          </form>
        </div>

      )}

      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>

        <Pagination
          count={Math.ceil(totalDocs / rowsPerPage)}
          page={page}                                  // current page
          onChange={(_, p) => setPage(p)}              // update page state
          color="primary"
        />
      </div>
      <ConversationDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        isAddingConversation={isAddingConversation}
        newConversation={newConversation}
        setNewConversation={setNewConversation}
        selectedConversations={selectedConversations}
        handleAddConversation={handleAddConversation}
        handleDeleteConversation={handleDeleteConversation}
      />
    </CustomerLayout>
  );
};
export default WithAuth(Customers);